/**
 * authApi — pure axios functions for authentication endpoints.
 *
 * These functions are intentionally free of React / Zustand state so they
 * can be called from React Query mutations/queries without side-effects.
 * State updates (store, navigation, toasts) are handled in useAuth hooks.
 */

import api from "./api_instance";
import { LoginFormType, LoginSuccessResponse } from "../types/login";
import { AuthUserProps } from "../types/auth";
import { API_ENDPOINTS } from "../constants/api";
import type {
  ForgotPasswordFormType,
  ForgotPasswordResponse,
} from "../types/forgotPassword";
import type {
  ResetPasswordPayload,
  ResetPasswordResponse,
} from "../types/resetPassword";
import type {
  ChangePasswordPayload,
  ChangePasswordResponse,
} from "../types/changePassword";

/** POST /auth/login */
export const loginApi = async (
  credentials: LoginFormType,
): Promise<LoginSuccessResponse> => {
  const { data } = await api.post<LoginSuccessResponse>(
    API_ENDPOINTS.AUTH.LOGIN,
    credentials,
  );
  return data;
};

/** POST /auth/forgot-password */
export const forgotPasswordApi = async (
  payload: ForgotPasswordFormType,
): Promise<ForgotPasswordResponse> => {
  const { data } = await api.post<ForgotPasswordResponse>(
    API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
    payload,
  );
  return data;
};

/** POST /auth/reset-password */
export const resetPasswordApi = async (
  payload: ResetPasswordPayload,
): Promise<ResetPasswordResponse> => {
  const { data } = await api.post<ResetPasswordResponse>(
    API_ENDPOINTS.AUTH.RESET_PASSWORD,
    payload,
  );
  return data;
};

export const resetPassword = resetPasswordApi;

/** GET /auth/me — requires valid Bearer token (injected by interceptor) */
export const getMeApi = async (): Promise<AuthUserProps> => {
  const { data } = await api.get<AuthUserProps>(API_ENDPOINTS.AUTH.ME);
  return data;
};

/** POST /auth/logout — best-effort; local state is cleared regardless */
export const logoutApi = async (refreshToken: string): Promise<void> => {
  await api.post(API_ENDPOINTS.AUTH.LOGOUT, { refresh_token: refreshToken });
};

/** POST /auth/change-password — requires valid Bearer token (injected by interceptor) */
export const changePasswordApi = async (
  payload: ChangePasswordPayload,
): Promise<ChangePasswordResponse> => {
  const { data } = await api.post<ChangePasswordResponse>(
    API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
    payload,
  );
  return data;
};
